// imports
import countries from 'i18n-iso-countries';
import CloseIcon from '@mui/icons-material/Close';
import { yupResolver } from '@hookform/resolvers/yup';
import enLocale from 'i18n-iso-countries/langs/en.json';
import React, { FC, useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Box, Button, CircularProgress, Grid, IconButton, Typography } from '@mui/material';
import { StripeCardCvcElementChangeEvent, StripeCardExpiryElementChangeEvent, StripeCardNumberElementChangeEvent } from '@stripe/stripe-js';
// components
import { CustomModal } from '../common/CustomModal';
import { Alert } from '../../../../shared/lib/common/Alert';
import { CustomController } from '../../../../shared/lib/common/CustomController';
import { StripeTextFieldCVC, StripeTextFieldExpiry, StripeTextFieldNumber } from '../StripeFields';
// context, reducer
import { useAppContext } from '../../app/contexts/AppContext';
import { ActionType } from '../../app/reducer/AppContextReducer';
// graphql
import { Card, useChangeCardMutation, useFetchBillingDetailsLazyQuery } from '../../generated';
// constants
import { MSG_CARD_CHANGED_SUCCESSFULLY, MSG_CARD_ELEMENT_NOT_FOUND, STRIPE_LOAD_ERROR_TEXT } from '../../app/constants';
import { BUTTON_TEXT_CANCEL, BUTTON_TEXT_SAVE, LABEL_NOT_AVAILABLE, LABEL_NAME_ON_CARD, LABEL_CARD_NUMBER, LABEL_EXPIRY_MM_YY, LABEL_CVC, LABEL_CARD_INFORMATION, MSG_FILL_INFO_TO_SAVE } from '../../../../shared/lib/constants';
// interfaces, pallete, schemas
import palette from '../../../../shared/lib/theme/palette';
import { stripePaymentSchema } from '../../app/validationSchema';
import { ChangeCardModalProps, StripeFormType } from '../../app/interfaceTypes';

const CardModal: FC<ChangeCardModalProps> = ({ refetch, setCardData }) => {
  const stripe = useStripe();
  const elements = useElements();
  countries.registerLocale(enLocale);

  const [stripeLoading, setStripeLoading] = useState<boolean>(false);
  const { isChangePlan, dispatch, currentUser } = useAppContext();
  const { blackOne, blackTwo, borderLight } = palette;
  const { isSubscribed } = currentUser ?? {};
  const { customerStripeId } = isSubscribed ?? {};
  const [address, setAddress] = useState<Card | undefined>(undefined);
  const { address_city, address_country, address_line1, address_line2, address_state, address_zip } = address ?? {};

  const handleClose = () => {
    dispatch({ type: ActionType.SET_IS_CHANGE_PLAN, isChangePlan: false });
    resetFormValues();
  };

  const methods = useForm<StripeFormType, any, StripeFormType>({
    defaultValues: {
      name: '',
      card: '',
      expiry: ''
    },
    resolver: yupResolver(stripePaymentSchema),
    mode: 'all'
  });
  const { handleSubmit, setError, formState, setValue } = methods;
  const { errors } = formState;
  const { card, expiry, cvc } = errors || {};

  const resetFormValues = () => {
    setValue('card', '');
    setValue('cvc', '');
    setValue('expiry', '');
    setValue('name', '');
  };

  const onCardChange = (event: StripeCardNumberElementChangeEvent) => {
    const { complete, error, empty } = event;
    const { message } = error || {};
    message && setError('card', { message, type: 'validate' });
    complete && !message && setError('card', {});
    empty &&
      setError('card', {
        message: 'Card No is requried',
        type: 'validate'
      });
  };

  const onExpiryChange = (event: StripeCardExpiryElementChangeEvent) => {
    const { complete, error, empty } = event;
    const { message } = error || {};
    message && setError('expiry', { message, type: 'validate' });
    complete && !message && setError('expiry', {});
    empty &&
      setError('expiry', {
        message: 'Expiry Date is Required',
        type: 'validate'
      });
  };

  const onCVCChange = (event: StripeCardCvcElementChangeEvent) => {
    const { complete, error, empty } = event;
    const { message } = error || {};
    message && setError('cvc', { message, type: 'validate' });
    complete && !message && setError('cvc', {});
    empty && setError('cvc', { message: 'cvc is required', type: 'validate' });
  };

  const [getBillingDetail] = useFetchBillingDetailsLazyQuery({
    onError({ message }) {
      Alert.error(message);
    },

    onCompleted(data) {
      const { fetchCardDetails } = data;
      if (fetchCardDetails) {
        setAddress(fetchCardDetails);
      }
    }
  });

  const [changeCard, { loading: changeCardLoading }] = useChangeCardMutation({
    onCompleted: (data) => {
      const { changeCard } = data;
      const { exp_month, last4, exp_year, name: cardName } = changeCard ?? {};
      if (changeCard) {
        if (cardName) {
          setCardData({ name: cardName, last4, expiry: String(exp_month).padStart(2, '0') + '/' + exp_year });
        } else {
          setCardData({ name: LABEL_NOT_AVAILABLE, last4, expiry: String(exp_month).padStart(2, '0') + '/' + exp_year });
        }
        Alert.success(MSG_CARD_CHANGED_SUCCESSFULLY);
        setStripeLoading(false);
        handleClose();
        resetFormValues();
        refetch && refetch();
      }
    },
    onError: ({ message }) => {
      Alert.error(message);
    }
  });

  const fetchBillingDetail = async (customerStripeId: string) => {
    await getBillingDetail({
      variables: {
        fetchCardDetailsInput: customerStripeId
      }
    });
  };

  const onSubmit: SubmitHandler<StripeFormType> = async (data) => {
    const { name } = data;
    if (!stripe) {
      Alert.error(STRIPE_LOAD_ERROR_TEXT);
    }
    if (customerStripeId && elements?.getElement) {
      setStripeLoading(true);

      const cardElement = elements.getElement(CardNumberElement);

      if (!cardElement) {
        Alert.error(MSG_CARD_ELEMENT_NOT_FOUND);
        return;
      }

      const res = await stripe?.createPaymentMethod({
        element: cardElement,
        params: {
          billing_details: {
            name,
            address: {
              country: countries.getAlpha2Code(address_country ?? '', 'en'),
              city: address_city ?? '',
              line1: address_line1 ?? '',
              line2: address_line2 ?? '',
              postal_code: address_zip ?? '',
              state: address_state ?? ''
            }
          }
        }
      });

      const { paymentMethod, error } = res || {};

      if (error) {
        const { message } = error;
        console.log(message);
        Alert.error(message || '');
        setStripeLoading(false);
      } else if (paymentMethod?.id) {
        await changeCard({
          variables: {
            changeCardInput: {
              token: paymentMethod.id,
              customerStripeId
            }
          }
        });
        setStripeLoading(false);
      }
    }
  };

  useEffect(() => {
    if (customerStripeId) {
      (async () => await fetchBillingDetail(customerStripeId))();
    }
  }, [customerStripeId]);

  const loading: boolean = stripeLoading || changeCardLoading;
  return (
    <CustomModal maxWidth="712px" isOpen={isChangePlan} handleClose={handleClose}>
      <FormProvider {...methods}>
        <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography py="24px" sx={{ color: blackOne }} variant="h6">
                {LABEL_CARD_INFORMATION}
              </Typography>

              <Typography variant="h2" color={blackTwo}>
                {MSG_FILL_INFO_TO_SAVE}
              </Typography>
            </Box>

            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Grid container spacing={2} rowSpacing={2}>
            <Grid item md={6} xs={12}>
              <CustomController controllerLabel={LABEL_NAME_ON_CARD} controllerName="name" />
            </Grid>
            <Grid item md={6} xs={12}>
              <StripeTextFieldNumber
                margin="dense"
                disabled={loading}
                label={LABEL_CARD_NUMBER}
                onChange={onCardChange}
                error={Boolean(card?.message)}
                labelErrorMessage={card?.message}
                inputProps={{
                  options: {
                    showIcon: true
                  }
                }}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <StripeTextFieldExpiry margin="dense" disabled={loading} label={LABEL_EXPIRY_MM_YY} onChange={onExpiryChange} error={Boolean(expiry?.message)} labelErrorMessage={expiry?.message} />
            </Grid>
            <Grid item md={6} xs={12}>
              <StripeTextFieldCVC margin="dense" label={LABEL_CVC} disabled={loading} onChange={onCVCChange} error={Boolean(cvc?.message)} labelErrorMessage={cvc?.message} />
            </Grid>
          </Grid>

          <Box display="flex" width="100%" gap="10px" mt="24px" mb="8px">
            <Button fullWidth onClick={handleClose} variant="text" sx={{ border: `1px solid ${borderLight}`, color: blackOne }}>
              {BUTTON_TEXT_CANCEL}
            </Button>
            <Button fullWidth variant="contained" type="submit" disabled={loading} endIcon={loading && <CircularProgress size={20} />}>
              {BUTTON_TEXT_SAVE}
            </Button>
          </Box>
        </form>
      </FormProvider>
    </CustomModal>
  );
};

export default CardModal;
